import { throttle } from 'throttle-debounce';

import { events, messageBus } from '@schibsted-nmp/advertising-events';

const FILTER_LIST_ID = 'advertising-tori-filters-bottom-marker';
const MIDDLE_2_ADVT_COMPONENT = 'advt-component[placementid="advt_middle_2"]';
const BANNERS_STICKY_CLASS_NAME_QUERY = '.banners__sticky';
const FINN_FOOTER_ELEM_QUERY = 'finn-footer-content';

let isRight1Locked = false;

const offset = 100;
let lockScrollPosition = 0;
let scrollListenerInitiated = false;

let filterListElement: HTMLElement | null = null;
let rightSkyscraperStickyContainer: HTMLElement | null = null;

type Placement = {
  element: HTMLElement | null;
  id: string;
};
const adnamiPlacements: Placement[] = [
  {
    id: 'footer',
    element: null
  }
];

function storeElementByPlacementId(placementId: string) {
  if (
    placementId &&
    !adnamiPlacements.some((data) => data.id === placementId)
  ) {
    adnamiPlacements.push({
      id: placementId,
      element: null
    });
  }
}

export function initiate() {
  listenToAdnamiPlacements();
  initiateIfAdnami();
}

function initiateIfAdnami() {
  const hasAdnamiAds = adnamiPlacements.length;
  if (hasAdnamiAds) {
    const throttledHandlePositionOverlapOnScroll = throttle(
      200,
      handlePositionOverlapOnScroll
    );

    if (!scrollListenerInitiated) {
      // Use the throttled function as the event listener
      window.addEventListener(
        'scroll',
        throttledHandlePositionOverlapOnScroll,
        { passive: true }
      );
      scrollListenerInitiated = true;
    }
  }
}

function listenToAdnamiPlacements() {
  messageBus.subscribe(events.PODLET.channel, 'adnami', (e) => {
    storeElementByPlacementId(e.payload as string);
    initiateIfAdnami();
  });
}

function getAdnamiElemIfCollidingWith(
  elem: HTMLElement | null
): Placement | null {
  if (!elem) return null;
  return (
    adnamiPlacements
      .filter((data) => data?.element)
      .find((data) => {
        if (checkCollision(elem, data.element, offset)) return data;
        return null;
      }) || null
  );
}

function toggleRight1Fixed() {
  if (!rightSkyscraperStickyContainer) return;
  const collidingPlacement = getAdnamiElemIfCollidingWith(
    rightSkyscraperStickyContainer
  );

  if (!isRight1Locked && collidingPlacement?.element) {
    setLockedAd(collidingPlacement.element!);
    isRight1Locked = true;
    return;
  }

  if (isRight1Locked) {
    const scrollDistanceSinceLock = window.scrollY - lockScrollPosition;

    if (scrollDistanceSinceLock < 0) {
      setScrollingAd();
      isRight1Locked = false;
    }
  }
}

function handlePositionOverlapOnScroll() {
  fetchRightStickyContainer();
  fetchFilterListElement();
  fetchAdnamiPlacements();

  if (rightSkyscraperStickyContainer) {
    toggleRight1Fixed();
  }
}

function checkCollision(
  element: HTMLElement | null,
  compareElement: HTMLElement | null,
  offsetValue = 0
) {
  if (!element || !compareElement) return false;
  const elemRect = element.getBoundingClientRect();
  const compareElemRect = compareElement.getBoundingClientRect();

  const elemBottom = elemRect.bottom;
  const compareElemTop = compareElemRect.top;

  if (elemBottom === 0 || compareElemTop === 0) return false;
  return !(compareElemTop > elemBottom + offsetValue);
}

function setLockedAd(adnamiElement?: HTMLElement) {
  if (!adnamiElement || !rightSkyscraperStickyContainer) return;

  const adnamiElementPosFromTop =
    adnamiElement.getBoundingClientRect().top +
    document.documentElement.scrollTop;
  const topPosition =
    adnamiElementPosFromTop -
    rightSkyscraperStickyContainer.offsetHeight -
    offset * 1.5;

  lockScrollPosition = topPosition;

  if (rightSkyscraperStickyContainer) {
    rightSkyscraperStickyContainer.style.position = 'absolute';
    rightSkyscraperStickyContainer.style.top = `${topPosition}px`;
  }
}

function setScrollingAd() {
  if (rightSkyscraperStickyContainer) {
    rightSkyscraperStickyContainer.style.position = 'fixed';
    rightSkyscraperStickyContainer.style.top = 'unset';
  }
}

function fetchAdnamiPlacements() {
  adnamiPlacements.forEach((placement) => {
    if (!placement.element) {
      if (placement.id === 'footer') {
        placement.element = document.querySelector(FINN_FOOTER_ELEM_QUERY);
      } else {
        placement.element = document.getElementById(placement.id);
      }
    }
  });
}

function fetchRightStickyContainer() {
  if (!rightSkyscraperStickyContainer) {
    rightSkyscraperStickyContainer = document.querySelector(
      BANNERS_STICKY_CLASS_NAME_QUERY
    );
  }
}

function fetchFilterListElement() {
  if (!filterListElement) {
    filterListElement = document.getElementById(FILTER_LIST_ID);
  }
}

export function publishAdnamiPlacement(placementId: string) {
  messageBus.publish(events.PODLET.channel, 'adnami', placementId);
}

export function middle2OverlapsWithFilter() {
  fetchFilterListElement();
  if (!filterListElement) return false;

  // Get the filter's bottom position relative to the viewport and add the current scroll position to get its position relative to the whole page
  const filterBottomPlacementViewport =
    filterListElement.getBoundingClientRect().bottom;

  if (!filterBottomPlacementViewport) return false;
  const filterBottomPlacement = filterBottomPlacementViewport + window.scrollY;

  const middle2Placement = document.querySelector(MIDDLE_2_ADVT_COMPONENT);

  // Get the ad placement's top position relative to the viewport and add the current scroll position to get its position relative to the whole page
  const middle2PlacementTopViewport =
    middle2Placement?.getBoundingClientRect().top;

  if (!middle2PlacementTopViewport) return false;
  const middle2PlacementTop = middle2PlacementTopViewport + window.scrollY;

  const margin = 100;
  const filterBottomPlacementWithMargin = filterBottomPlacement + margin;

  return middle2PlacementTop < filterBottomPlacementWithMargin;
}
